import { atom, atomFamily, CallbackInterface, DefaultValue, selector } from 'recoil';
import { loadPersistedValue, persistOnSet } from '../effects';
import { DrawFilter, DrawTool, FilterState, MapFilter } from '../../contexts/FilterContext/types';
import { selectedFloorPlanState } from './floorPlan';
import { mapBoundsState } from './mapMaintenance';
import { selectedRecordTypeState } from './menuSelections';
import { isEmpty } from 'lodash';

// #region Quicksearch
export const quickSearchState = atom<string>({
  key: 'filter.quickSearchState',
  default: '',
  effects: [loadPersistedValue, persistOnSet],
});

export const quickSearchFocusState = atom<boolean>({
  key: 'filter.quickSearchFocusState',
  default: false,
});
// #endregion

// #region Record Filter
export const recordFilterState = atomFamily<FilterState, string>({
  key: 'filter.recordFilterState',
  default: _recordType => ({}),
  effects: _recordType => [loadPersistedValue, persistOnSet],
});

export const getRecordFilterCallback =
  ({ snapshot }: CallbackInterface) =>
  (recordType: string): FilterState | undefined => {
    const { contents, state } = snapshot.getLoadable(recordFilterState(recordType)); // https://recoiljs.org/docs/api-reference/core/Loadable/
    return state === 'hasValue' ? contents ?? undefined : undefined;
  };

export const selectedRecordTypeFilterSelector = selector<FilterState>({
  key: 'filter.selectedRecordTypeFilterSelector',
  get: ({ get }) => {
    const selectedRecordType = get(selectedRecordTypeState);
    return get(recordFilterState(selectedRecordType));
  },
  set: ({ get, reset, set }, newValue) => {
    const selectedRecordType = get(selectedRecordTypeState);
    const filterState = recordFilterState(selectedRecordType);
    if (newValue instanceof DefaultValue) {
      reset(filterState);
    } else {
      set(filterState, newValue);
    }
  },
});

export const selectedRecordTypeFilterCountSelector = selector<number>({
  key: 'filter.selectedRecordTypeFilterCountSelector',
  get: ({ get }) => {
    const selectedRecordTypeFilter = get(selectedRecordTypeFilterSelector);
    return Object.keys(selectedRecordTypeFilter).length;
  },
});
// #endregion

// #region Map Extents
export const mapExtentFilterEnabledState = atom<MapFilter['extentFilterEnabled']>({
  key: 'filter.mapExtentFilterEnabledState',
  default: false,
  effects: [loadPersistedValue, persistOnSet],
});

export const mapFilterSelector = selector<MapFilter>({
  key: 'filter.mapFilterSelector',
  get: ({ get }) => {
    const extentFilterEnabled = get(mapExtentFilterEnabledState);
    const mapBounds = get(mapBoundsState);
    const selectedFloorPlanId = get(selectedFloorPlanState)?.id;
    return { extentFilterEnabled, mapBounds, selectedFloorPlanId };
  },
});
// #endregion

// #region Draw Filter
export const drawFilterEnabledState = atom<DrawFilter['enabled']>({
  key: 'filter.drawFilterEnabledState',
  default: false,
});

export const drawFilterFeaturesState = atom<DrawFilter['features']>({
  key: 'filter.drawFilterFeaturesState',
  default: [],
});

export const drawToolEnabledState = atom<DrawTool['enabled']>({
  key: 'filter.drawToolEnabledState',
  default: false,
});

// TODO: [S5-3741] Should this be a readonly selector based on `features.length`?
export const drawToolDrawnState = atom<DrawTool['drawn']>({
  key: 'filter.drawToolDrawnState',
  default: false,
});

export const drawFilterSelector = selector<DrawFilter>({
  key: 'filter.drawFilterSelector',
  get: ({ get }) => {
    const enabled = get(drawFilterEnabledState); // Formerly `drawFilter.apply`.
    const features = get(drawFilterFeaturesState);
    const toolEnabled = get(drawToolEnabledState); // Formerly `drawFilter.enabled`
    return { enabled, features, toolEnabled };
  },
  set: ({ set, reset }, newValue) => {
    if (newValue instanceof DefaultValue) {
      reset(drawFilterEnabledState);
      reset(drawFilterFeaturesState);
      reset(drawToolEnabledState);
      // reset(drawToolDrawnState);  // TODO: [S5-3741] Optimize; currently handled manually.
    } else {
      const { enabled, features, toolEnabled } = newValue;
      set(drawFilterEnabledState, enabled);
      set(drawFilterFeaturesState, features);
      set(drawToolEnabledState, toolEnabled);
      // set(drawToolDrawnState, features.length > 0); // TODO: [S5-3741] Optimize; currently handled manually.
    }
  },
});

// TODO: [S5-3741] Should this set individual atoms? For now, setting selector for closest parity to original method.
export const enableDrawFilterCallback =
  ({ set }: CallbackInterface) =>
  () => {
    set(drawFilterSelector, prev => ({
      ...prev,
      toolEnabled: true,
      enabled: false,
      features: prev.features.map(feature => {
        return { ...feature, properties: { color: '#FF00F5' }, existing: true };
      }),
    }));
  };

export const clearDrawFilterCallback =
  ({ set }: CallbackInterface) =>
  () => {
    set(drawToolDrawnState, false);
    set(drawFilterSelector, {
      toolEnabled: false,
      enabled: false,
      features: [],
    });
  };
// #endregion

// #region Current Filters
export const currentFilterCountSelector = selector<number>({
  key: 'filter.currentFilterCountSelector',
  get: ({ get }) => {
    const selectedRecordTypeFilterCount = get(selectedRecordTypeFilterCountSelector);
    const mapExtentFilterEnabled = get(mapExtentFilterEnabledState);
    const quickSearch = get(quickSearchState);
    return selectedRecordTypeFilterCount + (mapExtentFilterEnabled ? 1 : 0) + (!isEmpty(quickSearch) ? 1 : 0);
  },
});

export const resetCurrentFiltersCallback =
  ({ reset }: CallbackInterface) =>
  () => {
    reset(selectedRecordTypeFilterSelector);
    reset(mapExtentFilterEnabledState);
    reset(quickSearchState);
  };
// #endregion
